/**
 * Filler word detection and utilities for auto-removal feature.
 */
import {computeFillerPaddingSec} from "./computeFillerPaddingSec";

// Core filler words list
export const FILLER_WORDS = [
    'uh',
    'um', 
    'hm',
    'hmm',
    'mmm',
    'mm-hmm',
    'mm hmm',
    'ah',
    'er',
    'uhh',
    'umm',
] as const;

// Extended filler phrases (optional, can be toggled)
export const FILLER_PHRASES = [
    'you know',
    'i mean',
    'like',
    'so',
    'well',
    'but you know',
    'kind of',
    'sort of',
] as const;

// Categories for the UI selector
export interface FillerWordCategory {
    id: string;
    label: string;
    words: string[];
    count?: number;
}

export const FILLER_WORD_CATEGORIES: FillerWordCategory[] = [
    { id: 'uh', label: 'Uh', words: ['uh', 'uhh'] },
    { id: 'um', label: 'Um', words: ['um', 'umm'] },
    { id: 'hm', label: 'Hm', words: ['hm', 'hmm', 'mm-hmm', 'mm hmm', 'mhm'] },
    { id: 'mmm', label: 'Mmm', words: ['mmm', 'mm'] },
    { id: 'ah', label: 'Ah', words: ['ah', 'er'] },
];

interface FillerMatcher {
    categoryId: string;
    phrase: string;
    tokens: string[];
}

const FILLER_MATCHERS: FillerMatcher[] = FILLER_WORD_CATEGORIES
    .flatMap(category =>
        category.words.map(phrase => ({
            categoryId: category.id,
            phrase,
            tokens: phrase
                .split(/\s+/)
                .map(token => normalizeWord(token))
                .filter(Boolean),
        }))
    )
    // Prefer longest phrases when overlap exists (e.g. "you know what i mean" vs "you know")
    .sort((a, b) => b.tokens.length - a.tokens.length);

// ============ Filler Removal Tuning Constants ============

// Minimum gap to leave between words after filler removal (in seconds)
// 60ms is a good balance - shorter feels rushed, longer is noticeable silence
export const MIN_GAP_AFTER_CUT_SEC = 0.06; // 60ms

// Padding around filler words to ensure complete removal
export const MIN_PAD_SEC = 0.015;  // 15ms - always at least this much padding
export const MAX_PAD_SEC = 0.06;  // 60ms - don't go crazy eating into neighbors
export const BOUNDARY_PAD_SEC = 0.05; // 50ms - at start/end of transcript, can be aggressive

// Legacy constant for backwards compatibility
export const MIN_CROSSFADE_GAP_SEC = MIN_GAP_AFTER_CUT_SEC;

/**
 * Check if a word is a filler word.
 * Strips punctuation and normalizes for comparison.
 */
export function isFillerWord(word: string, enabledCategories?: Set<string>): boolean {
    const cleaned = normalizeWord(word);
    
    // If no categories provided (legacy callers), use the core list.
    if (!enabledCategories) {
        return FILLER_WORDS.includes(cleaned as any);
    }

    // If categories are provided but none are enabled, match nothing.
    if (enabledCategories.size === 0) {
        return false;
    }
    
    // Check only enabled categories
    for (const category of FILLER_WORD_CATEGORIES) {
        if (enabledCategories.has(category.id)) {
            if (category.words.includes(cleaned)) {
                return true;
            }
        }
    }
    
    return false;
}

/**
 * Normalize a word for comparison (lowercase, strip punctuation).
 */
export function normalizeWord(word: string): string {
    return (word || '')
        .toLowerCase()
        .replace(/[.,!?;:'"()[\]]/g, '')
        .trim();
}

/**
 * Get the category ID for a filler word.
 */
export function getFillerCategory(word: string): string | null {
    const cleaned = normalizeWord(word);
    
    for (const category of FILLER_WORD_CATEGORIES) {
        if (category.words.includes(cleaned)) {
            return category.id;
        }
    }
    
    return null;
}

/**
 * Information about a detected filler word occurrence.
 */
export interface FillerWordOccurrence {
    id: string;
    word: string;
    categoryId: string;
    startSec: number;  // Composition time
    endSec: number;    // Composition time
    srcStartSec: number;  // Source/clip-local time (for audio access)
    srcEndSec: number;    // Source/clip-local time (for audio access)
    // Which audio source this word belongs to (for audio analysis)
    sourceIndex: number;
    // Context: words before and after
    contextBefore: string[];
    contextAfter: string[];
    // Gap analysis (composition time - for UI/display and cuts)
    prevWordEndSec: number | null;
    nextWordStartSec: number | null;
    // Gap analysis (source time - for audio analysis)
    prevWordSrcEndSec: number | null;
    nextWordSrcStartSec: number | null;
    isHarshCut: boolean;
    // Computed padding for this filler (how much to extend the cut)
    paddingSec: number;
    // For repeated words detection
    isRepeatedWord?: boolean;
    repeatedWith?: string; // ID of the word it repeats
}

export interface FillerWordMatch {
    categoryId: string;
    phrase: string;
    wordCount: number;
}

/**
 * Find a filler match starting at a given word index.
 * Supports multi-word phrase matching.
 */
export function findFillerMatchAt(
    words: Array<{ text: string }>,
    startIndex: number,
    enabledCategories?: Set<string>
): FillerWordMatch | null {
    if (startIndex < 0 || startIndex >= words.length) return null;

    if (enabledCategories && enabledCategories.size === 0) {
        return null;
    }

    for (const matcher of FILLER_MATCHERS) {
        if (enabledCategories && !enabledCategories.has(matcher.categoryId)) {
            continue;
        }

        const len = matcher.tokens.length;
        if (len === 0) continue;
        if (startIndex + len > words.length) continue;

        let allMatch = true;
        for (let j = 0; j < len; j++) {
            const actual = normalizeWord(words[startIndex + j]?.text ?? '');
            if (actual !== matcher.tokens[j]) {
                allMatch = false;
                break;
            }
        }

        if (allMatch) {
            return {
                categoryId: matcher.categoryId,
                phrase: matcher.phrase,
                wordCount: len,
            };
        }
    }

    return null;
}

/**
 * Detect repeated consecutive words.
 * Returns pairs of word indices that are repeated.
 */
export function detectRepeatedWords(
    words: Array<{ text: string; startSec: number; endSec: number }>
): Array<{ firstIndex: number; secondIndex: number }> {
    const repeats: Array<{ firstIndex: number; secondIndex: number }> = [];
    
    for (let i = 0; i < words.length - 1; i++) {
        const current = normalizeWord(words[i].text);
        const next = normalizeWord(words[i + 1].text);
        
        // Skip very short words and common words
        if (current.length < 2) continue;
        if (['a', 'i', 'the', 'to', 'of', 'and', 'in', 'is', 'it'].includes(current)) continue;
        
        if (current === next) {
            repeats.push({ firstIndex: i, secondIndex: i + 1 });
        }
    }
    
    return repeats;
}

/**
 * Format timestamp as MM:SS
 */
export function formatTimestamp(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
}
